import type { RouteRecordRaw } from 'vue-router'

declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean
    requiresRole?: 'SUPER_ADMIN' | 'COMPANY_ADMIN'
    layout?: 'auth' | 'app'
    title?: string
    sidebarGroup?: 'main' | 'admin' | 'system'
  }
}

export const landingRoute: RouteRecordRaw = {
  path: '/',
  name: 'landing',
  component: () => import('@/views/LandingView.vue'),
  meta: { title: 'Automação WhatsApp para Afiliados' },
}

export const storefrontRoute: RouteRecordRaw = {
  path: '/loja/:slug',
  name: 'storefront',
  component: () => import('@/views/storefront/StorefrontView.vue'),
  meta: { title: 'Loja Virtual' },
}

export const authRoutes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: { layout: 'auth', title: 'Entrar' },
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('@/views/auth/RegisterView.vue'),
    meta: { layout: 'auth', title: 'Criar conta' },
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/views/auth/ForgotPasswordView.vue'),
    meta: { layout: 'auth', title: 'Recuperar senha' },
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('@/views/auth/ResetPasswordView.vue'),
    meta: { layout: 'auth', title: 'Definir nova senha' },
  },
]

export const appRoutes: RouteRecordRaw[] = [
  {
    path: '/app',
    // Host decide o layout: AppShell (web) ou MobileAppShell (app nativo Capacitor).
    component: () => import('@/components/layout/AppLayoutHost.vue'),
    meta: { requiresAuth: true, layout: 'app' },
    children: [
      {
        path: '',
        redirect: { name: 'dashboard' },
      },
      {
        path: 'dashboard',
        name: 'dashboard',
        component: () => import('@/views/app/DashboardView.vue'),
        meta: { title: 'Dashboard', sidebarGroup: 'main' },
      },
      {
        path: 'connections',
        name: 'connections',
        component: () => import('@/views/app/ConnectionsView.vue'),
        meta: { title: 'Conexões', sidebarGroup: 'main' },
      },
      {
        path: 'rules',
        name: 'rules',
        component: () => import('@/views/app/MirrorRulesView.vue'),
        meta: { title: 'Regras de espelhamento', sidebarGroup: 'main' },
      },
      {
        path: 'offers',
        name: 'offers',
        component: () => import('@/views/app/OffersView.vue'),
        meta: { title: 'Ofertas', sidebarGroup: 'main' },
      },
      {
        path: 'campaigns',
        name: 'campaigns',
        component: () => import('@/views/app/CampaignsView.vue'),
        meta: { title: 'Lotes', sidebarGroup: 'main' },
      },
      {
        path: 'agent',
        name: 'agent',
        component: () => import('@/views/app/AgentView.vue'),
        meta: { title: 'Agente IA', sidebarGroup: 'main' },
      },
      {
        path: 'templates',
        name: 'templates',
        component: () => import('@/views/app/TemplatesView.vue'),
        meta: { title: 'Templates', sidebarGroup: 'main' },
      },
      {
        path: 'affiliates',
        name: 'affiliates',
        component: () => import('@/views/app/AffiliatesView.vue'),
        meta: { title: 'Afiliados', sidebarGroup: 'main' },
      },
      {
        path: 'coupons',
        name: 'coupons',
        component: () => import('@/views/app/CouponsView.vue'),
        meta: { title: 'Cupons Mercado Livre', sidebarGroup: 'main' },
      },
      {
        path: 'short-links',
        name: 'short-links',
        component: () => import('@/views/app/ShortLinksView.vue'),
        meta: { title: 'Encurtador & Métricas', sidebarGroup: 'main' },
      },
      {
        path: 'smart-groups',
        name: 'smart-groups',
        component: () => import('@/views/app/SmartGroupsView.vue'),
        meta: { title: 'Grupos Inteligentes', sidebarGroup: 'main' },
      },
      {
        path: 'store',
        name: 'store-settings',
        component: () => import('@/views/app/StoreSettingsView.vue'),
        meta: { title: 'Minha Loja Virtual', sidebarGroup: 'main' },
      },
      {
        path: 'logs',
        name: 'logs',
        component: () => import('@/views/app/LogsView.vue'),
        meta: { title: 'Logs', sidebarGroup: 'main' },
      },
      {
        path: 'billing',
        name: 'billing',
        component: () => import('@/views/app/BillingView.vue'),
        meta: { title: 'Planos e cobrança', sidebarGroup: 'system' },
      },
      {
        path: 'settings',
        name: 'settings',
        component: () => import('@/views/app/SettingsView.vue'),
        meta: { title: 'Configurações', sidebarGroup: 'system' },
      },
      {
        path: 'processes',
        name: 'processes',
        component: () => import('@/views/app/ProcessesView.vue'),
        meta: { title: 'Fila e Processos', sidebarGroup: 'system' },
      },
      {
        path: 'backups',
        name: 'backups',
        component: () => import('@/views/app/BackupsView.vue'),
        meta: { title: 'Backups', sidebarGroup: 'system' },
      },
      {
        path: 'api-webhooks',
        name: 'api-webhooks',
        component: () => import('@/views/app/ApiWebhooksView.vue'),
        meta: { title: 'API e Webhooks', sidebarGroup: 'system' },
      },
      {
        path: 'help',
        name: 'help',
        component: () => import('@/views/app/HelpCenterView.vue'),
        meta: { title: 'Central de Ajuda & Lupyzinho', sidebarGroup: 'main' },
      },
    ],
  },
]

export const adminRoutes: RouteRecordRaw[] = [
  {
    path: '/admin',
    component: () => import('@/components/layout/AppShell.vue'),
    meta: { requiresAuth: true, requiresRole: 'SUPER_ADMIN', layout: 'app' },
    children: [
      {
        path: '',
        redirect: { name: 'admin-dashboard' },
      },
      {
        path: 'dashboard',
        name: 'admin-dashboard',
        component: () => import('@/views/admin/AdminDashboardView.vue'),
        meta: { title: 'Visão Geral · Painel SaaS', sidebarGroup: 'admin' },
      },
      {
        path: 'companies',
        name: 'admin-companies',
        component: () => import('@/views/admin/CompaniesAdminView.vue'),
        meta: { title: 'Empresas · Painel SaaS', sidebarGroup: 'admin' },
      },
      {
        path: 'finance',
        name: 'admin-finance',
        component: () => import('@/views/admin/AdminFinanceView.vue'),
        meta: { title: 'Financeiro & Faturas · Painel SaaS', sidebarGroup: 'admin' },
      },
      {
        path: 'plans',
        name: 'admin-plans',
        component: () => import('@/views/admin/PlansAdminView.vue'),
        meta: { title: 'Planos · Painel SaaS', sidebarGroup: 'admin' },
      },
      {
        path: 'users',
        name: 'admin-users',
        component: () => import('@/views/admin/UsersAdminView.vue'),
        meta: { title: 'Utilizadores globais · Painel SaaS', sidebarGroup: 'admin' },
      },
      {
        path: 'system',
        name: 'admin-system',
        component: () => import('@/views/admin/AdminSystemView.vue'),
        meta: { title: 'Infraestrutura & Saúde · Painel SaaS', sidebarGroup: 'admin' },
      },
      {
        path: 'audit',
        name: 'admin-audit',
        component: () => import('@/views/admin/AdminAuditView.vue'),
        meta: { title: 'Auditoria & Logs · Painel SaaS', sidebarGroup: 'admin' },
      },
    ],
  },
]

export const publicRedirectRoutes: RouteRecordRaw[] = [
  {
    path: '/g/:slug',
    name: 'public-smart-group-redirect',
    component: {
      template: '<div class="flex min-h-screen flex-col items-center justify-center gap-5 bg-canvas px-6 text-center dark:bg-night-bg"><div class="h-12 w-12 animate-spin rounded-full border-4 border-emerald-100 border-t-emerald-500 dark:border-emerald-950 dark:border-t-emerald-400"></div><div><p class="text-base font-bold text-ink dark:text-zinc-100">Preparando seu grupo...</p><p class="mt-1 text-sm text-zinc-500">Se esta tela continuar aberta, atualize a página.</p></div><button class="rounded-lg bg-emerald-500 px-4 py-2 text-sm font-bold text-white" onclick="window.location.reload()">Tentar novamente</button></div>',
    },
    meta: { title: 'Redirecionando...' },
  },
  {
    path: '/s/:slug',
    name: 'public-short-link-redirect',
    component: {
      template: '<div class="flex min-h-screen flex-col items-center justify-center gap-5 bg-canvas px-6 text-center dark:bg-night-bg"><div class="h-12 w-12 animate-spin rounded-full border-4 border-blue-100 border-t-brand dark:border-blue-950"></div><p class="text-sm font-semibold text-ink dark:text-zinc-100">Preparando seu link...</p><button class="rounded-lg bg-brand px-4 py-2 text-sm font-bold text-white" onclick="window.location.reload()">Tentar novamente</button></div>',
    },
    meta: { title: 'Redirecionando...' },
  },
]

export const notFoundRoute: RouteRecordRaw = {
  path: '/:pathMatch(.*)*',
  name: 'not-found',
  component: () => import('@/views/NotFoundView.vue'),
  meta: { layout: 'auth', title: 'Não encontrado' },
}
